import { X } from 'lucide-react'
import { useState } from 'react'
import { Navigate } from 'react-router'

import appleStore from '@/assets/marketing/fuelapp/apple-store.svg'
import fuelAppLogo from '@/assets/marketing/fuelapp/fuelapp-logo.svg'
import fuelAppLogoInverted from '@/assets/marketing/fuelapp/fuelapp-logo-inverted.svg'
import googlePlay from '@/assets/marketing/fuelapp/google-play.svg'
import referralCodePanel from '@/assets/marketing/fuelapp/referral-code-panel.png'
import referralHero from '@/assets/marketing/fuelapp/referral-hero.png'
import referralLocked from '@/assets/marketing/fuelapp/referral-locked.png'
import referralReceiptCredit from '@/assets/marketing/fuelapp/referral-receipt-credit.png'
import referralSignupCode from '@/assets/marketing/fuelapp/referral-signup-code.png'
import { Anchor } from '@/components/ui/Anchor'
import { trackMarketingCta } from '@/lib/analytics'
import { useHostGuard } from '@/lib/hostGuard'
import { cn } from '@/lib/utils'

/* One-off standalone landing page for the FuelApp referral program (see
   FuelApp.tsx). Ships its own FuelApp-branded header/footer and does NOT use
   the shared marketing chrome (MarketingLayout / SiteNav / SiteFooter). */

const HERO_CHECKS = [
  '$15 per referral, unlimited referrals',
  'Your friend gets $15 too',
  "Share a code, that's it",
]

const STEPS: {
  title: string
  paragraphs: string[]
  warning?: string
  afterWarning?: React.ReactNode
  image: string
  alt: string
  height: string
  reversed: boolean
}[] = [
  {
    title: 'Unlocking referrals',
    paragraphs: ['You can refer other drivers after you pump 75 gal with FuelApp.'],
    image: referralLocked,
    alt: 'Referrals aren’t unlocked yet — pump 75 more gallons to unlock referrals',
    height: 'clamp(220px,30vw,360px)',
    reversed: false,
  },
  {
    title: 'Sharing your referral code',
    paragraphs: [
      "Go to your Profile, then tap 'Earn referral credit' to find your unique code.",
      'You can share your code via text, QR code, or copy/paste to post on Facebook, Instagram, Reddit or anywhere else.',
    ],
    image: referralCodePanel,
    alt: 'Invite friends, get $15 — your referral code with QR code, Share and Copy code buttons',
    height: 'clamp(280px,30vw,360px)',
    reversed: true,
  },
  {
    title: 'Earning referral credit',
    paragraphs: [
      'Your friend will need to download FuelApp, then enter your referral code when they create their account.',
    ],
    warning: 'There is no way to add a referral code after an account is created.',
    afterWarning: (
      <>
        Once your friend hits 75 gal pumped with FuelApp, you’ll each earn a{' '}
        <strong className="font-extrabold text-[#050A12]">$15 credit</strong>.
      </>
    ),
    image: referralSignupCode,
    alt: 'Sign up screen with the ‘Have a referral code?’ link highlighted',
    height: 'clamp(260px,30vw,360px)',
    reversed: false,
  },
  {
    title: 'Spending referral credit',
    paragraphs: [
      "Credit will be applied automatically. We'll take $15 off as soon as your friend hits 75 gallons — even if that's on their very first purchase.",
    ],
    image: referralReceiptCredit,
    alt: 'Fuel receipt with a $15.00 referral credit highlighted',
    height: 'clamp(260px,30vw,360px)',
    reversed: true,
  },
]

const FAQS: { question: string; answer: React.ReactNode }[] = [
  {
    question: 'Who can I refer?',
    answer:
      'Refer anyone who does not already have a FuelApp account, either one they set up on their own or one managed by their fleet. (CloudTrucks drivers can refer other drivers, but cannot be referred.)',
  },
  {
    question: 'How can I tell if my credit was applied?',
    answer: "You'll see your referral credit on your fuel receipts, under Purchases.",
  },
  {
    question: 'I have more questions. Who can I talk to?',
    answer: (
      <>
        Give us a call at{' '}
        <a href="tel:+14694595311" className="font-bold text-[#050A12] hover:text-[#DB9200]">
          (469) 459-5311
        </a>{' '}
        or email us at{' '}
        <a href="mailto:support@fuel.app" className="font-bold text-[#050A12] hover:text-[#DB9200]">
          support@fuel.app
        </a>
        .
      </>
    ),
  },
]

function CheckIcon() {
  return (
    <span className="inline-flex size-5 flex-none items-center justify-center rounded-full bg-[#31AB6A] text-xs font-bold text-white">
      ✓
    </span>
  )
}

function AnnouncementBanner() {
  const [dismissed, setDismissed] = useState(false)
  if (dismissed) return null

  return (
    <div className="relative bg-[#050A12] px-[clamp(20px,3vw,32px)] py-3 text-center">
      <p className="mx-auto max-w-[860px] px-8 text-[clamp(16px,1.6vw,18px)] leading-[1.55] font-medium text-white">
        Referrals are rolling out now. We&apos;ll let you know when you have access.
      </p>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        className="absolute top-1/2 right-[clamp(20px,3vw,32px)] -translate-y-1/2 text-white/70 transition-colors hover:text-white"
      >
        <X className="size-4" />
      </button>
    </div>
  )
}

function DownloadLink({ cta }: { cta: string }) {
  return (
    <a
      href="#download"
      onClick={() => trackMarketingCta(cta)}
      className="inline-block rounded-[10px] bg-[#F6B100] px-5.5 py-[11px] text-[15px] font-bold tracking-[-0.01em] text-[#050A12] transition-[background,transform] duration-180 ease-in-out hover:-translate-y-px hover:bg-[#DB9200]"
    >
      Download
    </a>
  )
}

function FaqItem({
  question,
  answer,
  open,
  onToggle,
}: {
  question: string
  answer: React.ReactNode
  open: boolean
  onToggle: () => void
}) {
  return (
    <div className="border-b border-[#E1E7EE]">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="group flex w-full cursor-pointer items-center justify-between gap-6 py-[clamp(20px,2.4vw,26px)] text-left"
      >
        <span className="text-[clamp(17px,1.8vw,20px)] font-bold tracking-[-0.02em] text-[#050A12] transition-colors group-hover:text-[#DB9200]">
          {question}
        </span>
        <span className="flex-none text-2xl leading-none font-normal text-[#535C67]">{open ? '−' : '+'}</span>
      </button>
      <div
        className={cn(
          'overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out',
          open ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        )}
      >
        <div className="max-w-[68ch] pb-[clamp(22px,2.4vw,28px)] text-[clamp(16px,1.6vw,18px)] leading-[1.6] text-[#535C67]">
          {answer}
        </div>
      </div>
    </div>
  )
}

export function FuelAppReferral() {
  const [openIndex, setOpenIndex] = useState<number | null>(0)
  const { redirecting, navigateTo } = useHostGuard('marketing')
  if (navigateTo) return <Navigate to={navigateTo} replace />
  if (redirecting) return null

  return (
    <div className="bg-surface fuelapp-brand min-h-screen w-full shrink-0 font-sans text-[#050A12] antialiased">
      <title>Referral Program — FuelApp by Cai Labs</title>
      <meta
        name="description"
        content="Earn free fuel by sharing FuelApp with friends. Every driver who signs up with your code and pumps 75+ gallons earns $15 for you and $15 for them."
      />

      <AnnouncementBanner />

      <header className="sticky top-0 z-50 border-b border-[#E1E7EE] bg-white/80 backdrop-blur-[14px]">
        <div className="mx-auto flex max-w-[1180px] items-center justify-between gap-4 px-[clamp(20px,3vw,32px)] py-3.5">
          <Anchor href="/fuelapp">
            <img src={fuelAppLogo} alt="FuelApp" className="block h-[clamp(24px,3vw,30px)] w-auto" />
          </Anchor>
          <DownloadLink cta="fuelapp_referral_download_header" />
        </div>
      </header>

      <section className="mx-auto flex max-w-[1180px] flex-wrap items-center gap-[clamp(28px,5vw,64px)] px-[clamp(20px,3vw,32px)] pt-[clamp(40px,6vw,80px)] pb-[clamp(44px,5vw,64px)]">
        <div className="min-w-0 flex-1 basis-[380px]">
          <div className="mb-[clamp(16px,2vw,22px)] font-mono text-[13px] font-medium tracking-[0.06em] text-[#884A03] uppercase">
            FuelApp referral program
          </div>
          <h1 className="max-w-[17ch] text-[clamp(38px,5vw,56px)] leading-[1.04] font-extrabold tracking-[-0.035em] text-balance">
            Bring fuel costs down a little.
          </h1>
          <p className="mt-5 max-w-[46ch] text-[clamp(17px,1.6vw,19px)] leading-[1.55] text-[#535C67]">
            Earn free fuel by sharing FuelApp with friends. Every driver who signs up with your code and pumps
            75+ gallons earns $15 for you and $15 for them.
          </p>
          <div className="mt-7 flex flex-col gap-3.5">
            {HERO_CHECKS.map((label) => (
              <div key={label} className="flex items-center gap-2.5">
                <CheckIcon />
                <span className="font-mono text-[13px] font-medium text-[#21272F]">{label}</span>
              </div>
            ))}
          </div>
          <a
            href="https://mobile.fuel.app/referral"
            onClick={() => trackMarketingCta('fuelapp_referral_get_code_hero')}
            className="mt-8 inline-block rounded-xl bg-[#F6B100] px-9 py-4 text-[17px] font-bold tracking-[-0.01em] text-[#050A12] shadow-[0_12px_28px_-10px_rgba(246,177,0,0.7)] transition-[background,transform] duration-180 ease-in-out hover:-translate-y-0.5 hover:bg-[#DB9200]"
          >
            Get my referral code
          </a>
        </div>
        <div className="h-[clamp(260px,40vw,480px)] min-w-0 flex-1 basis-[380px] overflow-hidden rounded-3xl border border-[#E1E7EE] shadow-[0_30px_70px_-28px_rgba(5,10,18,0.32)]">
          <img
            src={referralHero}
            alt="Two drivers bumping fists at a truck stop"
            className="block h-full w-full object-cover"
          />
        </div>
      </section>

      <section className="border-y border-[#E1E7EE] bg-[#F8FAFE]">
        <div className="mx-auto max-w-[1180px] px-[clamp(20px,3vw,32px)] py-[clamp(48px,6vw,84px)]">
          <h2 className="mb-[clamp(32px,4vw,56px)] text-[clamp(30px,3.4vw,40px)] font-extrabold tracking-[-0.03em]">
            How it works
          </h2>
          <div className="flex flex-col gap-[clamp(44px,5vw,64px)]">
            {STEPS.map((step, i) => (
              <div key={step.title} className="flex flex-wrap items-center gap-[clamp(20px,4vw,56px)]">
                <div
                  className={cn(
                    'bg-surface flex min-w-0 flex-1 basis-[340px] items-center justify-center overflow-hidden rounded-[clamp(22px,3vw,32px)] border border-[#E1E7EE] p-[clamp(18px,2.4vw,28px)]',
                    step.reversed && 'order-2'
                  )}
                  style={{ height: step.height }}
                >
                  <img
                    src={step.image}
                    alt={step.alt}
                    className="block max-h-full max-w-full rounded-xl object-contain"
                  />
                </div>
                <div className={cn('min-w-0 flex-1 basis-[340px]', step.reversed && 'order-1')}>
                  <div className="flex items-center gap-3">
                    <span className="inline-flex size-[30px] flex-none items-center justify-center rounded-[9px] bg-[#050A12] font-mono text-sm font-bold text-white">
                      {i + 1}
                    </span>
                    <h3 className="text-[clamp(21px,2.2vw,26px)] font-extrabold tracking-[-0.02em]">
                      {step.title}
                    </h3>
                  </div>
                  {step.paragraphs.map((p) => (
                    <p
                      key={p}
                      className="mt-4 max-w-[46ch] text-[clamp(16px,1.6vw,18px)] leading-[1.55] text-[#535C67]"
                    >
                      {p}
                    </p>
                  ))}
                  {step.warning && (
                    <div className="mt-5 max-w-[46ch] rounded-[18px] border border-[#FBEEC9] bg-[#FCF7E7] px-[22px] py-[18px]">
                      <p className="text-[clamp(15px,1.5vw,17px)] leading-[1.5] font-bold tracking-[-0.01em] text-[#050A12]">
                        ⚠️ {step.warning}
                      </p>
                    </div>
                  )}
                  {step.afterWarning && (
                    <p className="mt-5 max-w-[46ch] text-[clamp(16px,1.6vw,18px)] leading-[1.55] text-[#535C67]">
                      {step.afterWarning}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1180px] px-[clamp(20px,3vw,32px)] py-[clamp(48px,6vw,84px)]">
        <h2 className="mb-[clamp(24px,3vw,40px)] text-[clamp(30px,3.4vw,40px)] font-extrabold tracking-[-0.03em]">
          FAQs
        </h2>
        <div className="border-t border-[#E1E7EE]">
          {FAQS.map((faq, i) => (
            <FaqItem
              key={faq.question}
              question={faq.question}
              answer={faq.answer}
              open={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? null : i)}
            />
          ))}
        </div>
      </section>

      <section id="download" className="bg-[#F6B100]">
        <div className="mx-auto max-w-[1180px] px-[clamp(20px,3vw,32px)] py-[clamp(52px,6vw,88px)] text-center">
          <h2 className="text-[clamp(32px,4vw,46px)] font-extrabold tracking-[-0.03em] text-balance text-[#050A12]">
            Share FuelApp, earn free fuel
          </h2>
          <p className="mt-4 text-[clamp(17px,1.8vw,20px)] font-medium text-[#6C380B]">
            Download FuelApp and start saving today.
          </p>
          <div className="mt-[clamp(26px,3vw,38px)] flex flex-wrap justify-center gap-3.5">
            <a
              href="https://mobile.fuel.app/vFhe2PcwQ6b"
              onClick={() => trackMarketingCta('fuelapp_referral_app_store')}
              className="block transition-transform duration-180 hover:-translate-y-0.5"
            >
              <img src={appleStore} alt="Download on the App Store" className="block h-13.5" />
            </a>
            <a
              href="https://mobile.fuel.app/ghNQSA5vQ6b"
              onClick={() => trackMarketingCta('fuelapp_referral_google_play')}
              className="block transition-transform duration-180 hover:-translate-y-0.5"
            >
              <img src={googlePlay} alt="Get it on Google Play" className="block h-13.5" />
            </a>
          </div>
        </div>
      </section>

      <footer className="bg-neutral-900">
        <div className="mx-auto max-w-[1180px] px-[clamp(20px,3vw,32px)] pt-[clamp(40px,5vw,56px)] pb-10">
          <img src={fuelAppLogoInverted} alt="FuelApp" className="block h-[clamp(24px,2.6vw,28px)] w-auto" />
          <div className="mt-[clamp(28px,3vw,38px)] flex flex-wrap items-center justify-between gap-4 border-t border-[#21272F] pt-6">
            <span className="font-mono text-xs text-[#737D89]">© 2026 FuelApp by Cai Labs</span>
            <div className="flex gap-5">
              <a
                href="/terms"
                className="font-mono text-xs text-[#9BA4AF] no-underline transition-colors duration-150 hover:text-[#F6B100]"
              >
                Terms
              </a>
              <a
                href="/privacy"
                className="font-mono text-xs text-[#9BA4AF] no-underline transition-colors duration-150 hover:text-[#F6B100]"
              >
                Privacy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
